import { type FormEvent, type MouseEvent, type ReactElement, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFieldErrors } from '../../shared/hooks/useFieldErrors.js';
import { useQuickFilter } from '../../shared/hooks/useQuickFilter.js';
import { useDoorTransition } from '../../shared/illustrations/DoorTransition.js';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import {
  Button,
  Card,
  ConfirmButton,
  Dialog,
  EmptyState,
  ErrorAlert,
  Field,
  FilterInput,
  LoadingBlock,
  PageHeader,
  TextInput,
} from '../../shared/ui/index.js';
import { useCreateHouse, useDeleteHouse, useHouses } from './houseHooks.js';

export function HousesPage(): ReactElement {
  const t = useTranslation();
  const houses = useHouses();
  const deleteHouse = useDeleteHouse();
  const doorTransition = useDoorTransition();
  const [creating, setCreating] = useState(false);
  const filter = useQuickFilter(houses.data ?? [], (house) => [house.name, house.address]);

  // A plain navigation click gets the door-opening flourish first; a
  // modified click (new tab, new window) is left completely alone.
  const enterHouse =
    (houseId: string, houseName: string) =>
    (event: MouseEvent<HTMLAnchorElement>): void => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      event.preventDefault();
      doorTransition.enter(`/houses/${houseId}`, houseName);
    };

  return (
    <div className="stack stack--loose">
      <PageHeader
        title={t.housesPage.title}
        subtitle={t.housesPage.subtitle}
        actions={<Button onClick={() => setCreating(true)}>{t.housesPage.addHouse}</Button>}
      />

      <ErrorAlert error={houses.error ?? deleteHouse.error} />

      {houses.isPending ? <LoadingBlock /> : null}

      {houses.data !== undefined && houses.data.length > 0 ? (
        <FilterInput
          label={t.housesPage.filterLabel}
          placeholder={t.housesPage.filterPlaceholder}
          value={filter.query}
          matchCount={filter.matchCount}
          totalCount={filter.totalCount}
          onChange={filter.setQuery}
        />
      ) : null}

      {houses.data?.length === 0 ? (
        <EmptyState
          title={t.housesPage.emptyTitle}
          description={t.housesPage.emptyDescription}
          action={<Button onClick={() => setCreating(true)}>{t.housesPage.addHouse}</Button>}
        />
      ) : null}

      {filter.isFiltering && filter.matchCount === 0 ? (
        <EmptyState
          title={t.housesPage.noMatchesTitle}
          description={t.housesPage.noMatchesForQuery(filter.query)}
          action={
            <Button variant="secondary" onClick={() => filter.setQuery('')}>
              {t.common.clearFilter}
            </Button>
          }
        />
      ) : null}

      {filter.matchCount > 0 ? (
        <div className="grid">
          {filter.results.map((house) => (
            <Card key={house.id}>
              <div className="stack stack--tight">
                <div className="row row--between">
                  <h2 className="card__title">
                    <Link to={`/houses/${house.id}`} onClick={enterHouse(house.id, house.name)}>
                      {house.name}
                    </Link>
                  </h2>
                  <ConfirmButton
                    small
                    label={t.common.delete}
                    onConfirm={() => deleteHouse.mutate(house.id)}
                  />
                </div>
                <p className="card__meta">{house.address ?? t.common.noAddressRecorded}</p>
                <p className="card__meta">
                  {t.count.rooms(house.roomCount)} · {t.count.beds(house.bedCount)}
                </p>
              </div>
            </Card>
          ))}
        </div>
      ) : null}

      {creating ? <CreateHouseDialog onClose={() => setCreating(false)} /> : null}
    </div>
  );
}

function CreateHouseDialog({ onClose }: { onClose: () => void }): ReactElement {
  const t = useTranslation();
  const createHouse = useCreateHouse();
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const fieldError = useFieldErrors(createHouse.error);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    createHouse.mutate(
      { name, address: address.trim() === '' ? null : address },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog title={t.housesPage.dialogTitle} onClose={onClose}>
      <form className="stack" onSubmit={submit} noValidate>
        <ErrorAlert error={createHouse.error} />
        <Field
          label={t.sojournsPage.nameLabel}
          hint={t.housesPage.nameHint}
          error={fieldError('name')}
        >
          {(props) => (
            <TextInput
              {...props}
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoFocus
              required
            />
          )}
        </Field>
        <Field
          label={t.housesPage.addressLabel}
          hint={t.housesPage.addressHint}
          error={fieldError('address')}
        >
          {(props) => (
            <TextInput
              {...props}
              value={address}
              onChange={(event) => setAddress(event.target.value)}
            />
          )}
        </Field>
        <div className="row row--end">
          <Button variant="secondary" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button type="submit" loading={createHouse.isPending}>
            {t.housesPage.addHouseSubmit}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
